import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
    View,
    KeyboardAvoidingView,
    Platform,
    Linking,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { useAppSelector, useAppDispatch } from './src/store/hooks'
import {
    selectIsAuthenticated,
    selectCanManageTables,
    selectCurrentUser,
    selectRegistrationPending,
    selectPendingRegistrationEmail,
    selectPendingRegistrationPhone,
} from './src/store/selectors/authSelectors'
import SplashScreen from './src/components/SplashScreen'
import LoginForm from './src/components/LoginForm'
import ServiceScoreBoard from './src/components/ServiceScoreBoard'
import type { DashboardOverlay } from './src/components/Dashboard'
import type { PendingBookingReviewContext, BookingEditContext } from './src/types/markerNotification.types'
import AppStyles from './App.styles'

// Lazy-loaded: kiosk devices (old iPads running only the /login -> /score
// flow via the SERVICE role) must never have to download these chunks.
const RegisterForm = React.lazy(() => import('./src/components/RegisterForm'))
const RegistrationPendingScreen = React.lazy(() => import('./src/components/RegistrationPendingScreen'))
const PasswordResetForm = React.lazy(() => import('./src/components/PasswordResetForm'))
const UserCabinetScreen = React.lazy(() => import('./src/components/UserCabinetScreen'))
const BookingScreen = React.lazy(() => import('./src/components/booking/BookingScreen'))
const StaffRivalSearchScreen = React.lazy(() => import('./src/components/rivalSearch/StaffRivalSearchScreen'))
const Dashboard = React.lazy(() => import('./src/components/Dashboard'))
const UserHomeScreen = React.lazy(() => import('./src/components/UserHomeScreen'))
const UserSplashScreen = React.lazy(() => import('./src/components/UserSplashScreen'))
const NotificationCenter = React.lazy(() => import('./src/components/NotificationCenter'))
const UserDetailsForm = React.lazy(() => import('./src/components/UserDetailsForm'))
const ActivationSetupForm = React.lazy(() => import('./src/components/ActivationSetupForm'))
const LandingPage = React.lazy(() => import('./src/components/LandingPage'))
import { logout, registerSocial, loginSocial, refreshRegistrationStatus } from './src/store/slices/authSlice'
import { addNotification, setStaffViewMode } from './src/store/slices/uiSlice'
import LocaleSwitcher from './src/components/LocaleSwitcher'
import { selectAppLocale } from './src/store/selectors/localeSelectors'
import { selectIsStaffUserView } from './src/store/selectors/uiSelectors'
import { resolveShowPending, resolveShowProfileForm } from './src/utils/appNavigationState'
import {
    PATHS,
    isStaffShellPath,
    roleHomePath,
} from './src/navigation/paths'

const OVERLAY_PATH: Record<DashboardOverlay, string> = {
    bills: PATHS.bills,
    users: PATHS.users,
    reports: PATHS.reports,
    settings: PATHS.settings,
    notifications: PATHS.notifications,
    ratings: PATHS.ratings,
    video: PATHS.video,
}

export default function App() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const locale = useAppSelector(selectAppLocale)
    const isAuthenticated = useAppSelector(selectIsAuthenticated)
    const canManageTables = useAppSelector(selectCanManageTables)
    const isStaffUserView = useAppSelector(selectIsStaffUserView)
    const currentUser = useAppSelector(selectCurrentUser)
    const registrationPending = useAppSelector(selectRegistrationPending)
    const pendingEmail = useAppSelector(selectPendingRegistrationEmail)
    const pendingPhone = useAppSelector(selectPendingRegistrationPhone)

    const [bootSplash, setBootSplash] = useState(true)
    const [confirmationToken, setConfirmationToken] = useState<string | null>(null)
    const [activationToken, setActivationToken] = useState<string | null>(null)
    const [resetToken, setResetToken] = useState<string | null>(null)
    const [telegramInviteToken, setTelegramInviteToken] = useState<string | null>(null)
    const [pendingBookingReview, setPendingBookingReview] = useState<PendingBookingReviewContext | null>(null)
    const [bookingEditContext, setBookingEditContext] = useState<BookingEditContext | null>(null)
    const [showUserSplash, setShowUserSplash] = useState(false)
    const hasObservedAuthStateRef = useRef(false)
    const previousIsAuthenticatedRef = useRef(isAuthenticated)
    const previousShowUserLandingRef = useRef(false)
    const previousProfileCompletedRef = useRef(false)
    const oauthHandledRef = useRef(false)

    const clearRegistrationUrl = useCallback(() => {
        if (typeof window === 'undefined') {
            return
        }
        const url = new URL(window.location.href)
        if (url.pathname.includes('/user/details/complete') || url.searchParams.has('token')) {
            navigate(PATHS.splash, { replace: true })
        }
    }, [navigate])

    useEffect(() => {
        const timer = setTimeout(() => setBootSplash(false), 2000)
        return () => clearTimeout(timer)
    }, [])

    const handleOAuthCallback = useCallback(async (url: string) => {
        let parsed: URL
        try {
            parsed = new URL(url)
        } catch {
            return
        }
        const match = parsed.pathname.match(/^\/oauth\/(google|apple)\/callback$/i)
            || (parsed.protocol === 'poolcontrol:' && parsed.host === 'oauth'
                ? parsed.pathname.match(/^\/(google|apple)\/callback$/i)
                : null)
        const code = parsed.searchParams.get('code')
        if (!match || !code || oauthHandledRef.current) {
            return
        }
        oauthHandledRef.current = true
        const provider = match[1].toLowerCase() as 'google' | 'apple'
        const redirectUri = typeof window !== 'undefined'
            ? `${window.location.origin}/oauth/${provider}/callback`
            : `poolcontrol://oauth/${provider}/callback`
        const oauthMode = typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem('oauthMode') || 'register'
            : 'register'
        const storedLocale = typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem('poolcontrol.locale')
            : null
        const oauthLocale = storedLocale === 'en' || storedLocale === 'ua' ? storedLocale : 'ua'

        try {
            if (oauthMode === 'login') {
                await dispatch(loginSocial({ provider, authorizationCode: code, redirectUri })).unwrap()
                dispatch(addNotification({ type: 'success', message: 'Signed in successfully.' }))
            } else {
                await dispatch(registerSocial({
                    provider,
                    authorizationCode: code,
                    redirectUri,
                    locale: oauthLocale,
                })).unwrap()
                dispatch(addNotification({ type: 'success', message: 'Social registration complete.' }))
            }
        } catch {
            dispatch(addNotification({
                type: 'error',
                message: oauthMode === 'login' ? 'Social login failed.' : 'Social registration failed.',
            }))
        } finally {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.removeItem('oauthMode')
            }
            navigate(PATHS.splash, { replace: true })
        }
    }, [dispatch, navigate])

    useEffect(() => {
        const token = searchParams.get('token')
        const activation = searchParams.get('activationToken')
        const reset = searchParams.get('resetToken')
        const tgReg = searchParams.get('tgReg')

        if (token) {
            setConfirmationToken(token)
            clearRegistrationUrl()
        }
        if (activation) {
            setActivationToken(activation)
        }
        if (reset) {
            setResetToken(reset)
        }
        if (tgReg) {
            setTelegramInviteToken(tgReg)
            const next = new URLSearchParams(searchParams)
            next.delete('tgReg')
            setSearchParams(next, { replace: true })
            navigate(PATHS.register, { replace: true })
        }
        const ytConnected = searchParams.get('ytConnected')
        if (ytConnected !== null) {
            const next = new URLSearchParams(searchParams)
            next.delete('ytConnected')
            setSearchParams(next, { replace: true })
            if (ytConnected === 'true') {
                dispatch(addNotification({ type: 'success', message: 'YouTube account connected successfully.' }))
            }
        }
        if (typeof window !== 'undefined') {
            handleOAuthCallback(window.location.href)
        }
    }, [searchParams, setSearchParams, clearRegistrationUrl, handleOAuthCallback, navigate, dispatch])

    useEffect(() => {
        const sub = Linking.addEventListener('url', ({ url }) => {
            handleOAuthCallback(url)
        })
        Linking.getInitialURL().then((url) => {
            if (url) {
                handleOAuthCallback(url)
            }
        })
        return () => sub.remove()
    }, [handleOAuthCallback])

    useEffect(() => {
        if (isAuthenticated && registrationPending) {
            dispatch(refreshRegistrationStatus())
        }
    }, [isAuthenticated, registrationPending, dispatch])

    const showProfileForm = resolveShowProfileForm({
        isAuthenticated,
        profileCompleted: Boolean(currentUser?.profileCompleted),
        registrationPending,
        confirmationToken,
        activationToken,
        resetToken,
    })

    const showPending = resolveShowPending({
        isAuthenticated,
        registrationPending,
        confirmationToken,
        activationToken,
        resetToken,
    })

    const profileReady = Boolean(isAuthenticated && currentUser?.profileCompleted && !showPending)
    const showStaffDashboard = profileReady && canManageTables && !isStaffUserView
    const showStaffUserView = profileReady && canManageTables && isStaffUserView
    const showUserHome = profileReady && !canManageTables
    const showUserLanding = showUserHome || showStaffUserView
    const isProfileCompleted = Boolean(currentUser?.profileCompleted)
    const isService = currentUser?.role === 'SERVICE'
    const homePath = roleHomePath(canManageTables, isService)

    useLayoutEffect(() => {
        if (!hasObservedAuthStateRef.current) {
            hasObservedAuthStateRef.current = true
            previousIsAuthenticatedRef.current = isAuthenticated
            previousShowUserLandingRef.current = showUserLanding
            previousProfileCompletedRef.current = isProfileCompleted
            return
        }

        const wasAuthenticated = previousIsAuthenticatedRef.current
        const wasProfileCompleted = previousProfileCompletedRef.current

        if (wasAuthenticated && !isAuthenticated) {
            setPendingBookingReview(null)
            setBookingEditContext(null)
            setShowUserSplash(false)
            setConfirmationToken(null)
            setActivationToken(null)
            setResetToken(null)
            setTelegramInviteToken(null)
            navigate(PATHS.splash, { replace: true })
        } else if (!wasAuthenticated && isAuthenticated) {
            setActivationToken(null)
            setResetToken(null)
            const next = new URLSearchParams(searchParams)
            let changed = false
            if (next.has('activationToken')) {
                next.delete('activationToken')
                changed = true
            }
            if (next.has('resetToken')) {
                next.delete('resetToken')
                changed = true
            }
            if (changed) {
                setSearchParams(next, { replace: true })
            }
            if (isService && isProfileCompleted) {
                navigate(PATHS.score, { replace: true })
            } else if (showUserLanding) {
                setShowUserSplash(true)
            } else if (canManageTables && isProfileCompleted) {
                navigate(PATHS.dashboard, { replace: true })
            }
        } else if (
            wasAuthenticated
            && isAuthenticated
            && !wasProfileCompleted
            && isProfileCompleted
            && showUserLanding
        ) {
            setShowUserSplash(true)
        } else if (
            wasAuthenticated
            && isAuthenticated
            && showUserLanding
            && !previousShowUserLandingRef.current
            && !canManageTables
        ) {
            setShowUserSplash(true)
        }

        previousIsAuthenticatedRef.current = isAuthenticated
        previousShowUserLandingRef.current = showUserLanding
        previousProfileCompletedRef.current = isProfileCompleted
    }, [
        isAuthenticated,
        showUserLanding,
        isProfileCompleted,
        canManageTables,
        navigate,
        searchParams,
        setSearchParams,
    ])

    useEffect(() => {
        if (!profileReady || !canManageTables || showUserSplash) {
            return
        }
        if (isStaffUserView && isStaffShellPath(location.pathname)) {
            navigate(PATHS.home, { replace: true })
        } else if (!isStaffUserView && location.pathname === PATHS.home) {
            navigate(PATHS.dashboard, { replace: true })
        }
    }, [
        profileReady,
        canManageTables,
        isStaffUserView,
        location.pathname,
        navigate,
        showUserSplash,
    ])

    useEffect(() => {
        if (!profileReady || showUserSplash) {
            return
        }
        if (location.pathname === PATHS.login || location.pathname === PATHS.register) {
            navigate(homePath, { replace: true })
        }
    }, [profileReady, showUserSplash, location.pathname, navigate, homePath])

    const blockingAuthFlow = showPending || showProfileForm
        || (!isAuthenticated && Boolean(activationToken))
        || (!isAuthenticated && Boolean(resetToken))

    const showLocaleToolbar = useMemo(() => {
        if (showStaffDashboard && isStaffShellPath(location.pathname)) {
            return false
        }
        if (location.pathname === PATHS.splash) {
            return false
        }
        if (location.pathname === PATHS.home || location.pathname === PATHS.score) {
            return false
        }
        if (
            !isAuthenticated
            && !confirmationToken
            && !activationToken
            && !resetToken
            && !showPending
            && (location.pathname === PATHS.login || location.pathname === PATHS.register)
        ) {
            return false
        }
        return true
    }, [
        showStaffDashboard,
        location.pathname,
        isAuthenticated,
        confirmationToken,
        activationToken,
        resetToken,
        showPending,
    ])

    const clearActivationTokenFromUrl = useCallback(() => {
        setActivationToken(null)
        const next = new URLSearchParams(searchParams)
        next.delete('activationToken')
        setSearchParams(next, { replace: true })
    }, [searchParams, setSearchParams])

    const clearResetTokenFromUrl = useCallback(() => {
        setResetToken(null)
        const next = new URLSearchParams(searchParams)
        next.delete('resetToken')
        setSearchParams(next, { replace: true })
        navigate(PATHS.login, { replace: true })
    }, [searchParams, setSearchParams, navigate])

    const openBookings = useCallback((opts?: {
        review?: PendingBookingReviewContext | null
        edit?: BookingEditContext | null
    }) => {
        setPendingBookingReview(opts?.review ?? null)
        setBookingEditContext(opts?.edit ?? null)
        navigate(PATHS.bookings, { replace: true })
    }, [navigate])

    const goStaffUserView = useCallback(() => {
        dispatch(setStaffViewMode('user'))
        navigate(PATHS.home)
    }, [dispatch, navigate])

    const renderStaffDashboard = (overlay: DashboardOverlay | null) => (
        <View style={AppStyles.appContent}>
            <Dashboard
                activeOverlay={overlay}
                onOpenOverlay={(next) => navigate(OVERLAY_PATH[next])}
                onCloseOverlay={() => navigate(PATHS.dashboard, { replace: true })}
                onOpenBookings={() => openBookings()}
                onOpenBookingReview={(context) => openBookings({ review: context })}
                onOpenBookingEdit={(context) => openBookings({ edit: context })}
                onOpenRivalSearch={() => navigate(PATHS.rivalSearch, { replace: true })}
                onStaffUserView={goStaffUserView}
            />
        </View>
    )

    const requireStaff = (element: React.ReactElement) => {
        if (!isAuthenticated) {
            return <Navigate to={PATHS.login} replace />
        }
        if (!profileReady) {
            return null
        }
        if (!canManageTables) {
            return <Navigate to={PATHS.home} replace />
        }
        if (isStaffUserView) {
            return <Navigate to={PATHS.home} replace />
        }
        return element
    }

    const requireService = (element: React.ReactElement) => {
        if (!isAuthenticated) return <Navigate to={PATHS.login} replace />
        if (!profileReady) return null
        if (!isService) return <Navigate to={homePath} replace />
        return element
    }

    const requireUserLanding = (element: React.ReactElement) => {
        if (!isAuthenticated) {
            return <Navigate to={PATHS.login} replace />
        }
        if (!profileReady) {
            return null
        }
        if (isService) {
            return <Navigate to={PATHS.score} replace />
        }
        if (canManageTables && !isStaffUserView) {
            return <Navigate to={PATHS.dashboard} replace />
        }
        return element
    }

    if (bootSplash) {
        return <SplashScreen />
    }

    if (showUserLanding && showUserSplash && currentUser) {
        return (
            <SafeAreaView style={AppStyles.safeArea} edges={['top']}>
                <React.Suspense fallback={<SplashScreen />}>
                    <NotificationCenter />
                    <UserSplashScreen
                        userName={currentUser.fullName}
                        onComplete={() => {
                            setShowUserSplash(false)
                            navigate(homePath, { replace: true })
                        }}
                    />
                </React.Suspense>
            </SafeAreaView>
        )
    }

    return (
        <SafeAreaView style={AppStyles.safeArea} edges={['top']}>
            <KeyboardAvoidingView
                style={AppStyles.flex1}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            >
                {showLocaleToolbar ? (
                    <View style={AppStyles.localeToolbar}>
                        <LocaleSwitcher />
                    </View>
                ) : null}
                <React.Suspense fallback={<SplashScreen />}>
                <NotificationCenter />

                {showPending && (
                    <RegistrationPendingScreen
                        onDone={() => navigate(PATHS.splash)}
                        registrationEmail={pendingEmail ?? currentUser?.email ?? undefined}
                        registrationPhone={pendingPhone ?? currentUser?.phone ?? undefined}
                    />
                )}

                {showProfileForm && (
                    <UserDetailsForm
                        confirmationToken={confirmationToken}
                        onDone={() => {
                            setConfirmationToken(null)
                            clearRegistrationUrl()
                        }}
                    />
                )}

                {!isAuthenticated && activationToken && (
                    <ActivationSetupForm
                        activationToken={activationToken}
                        onDone={clearActivationTokenFromUrl}
                    />
                )}

                {!isAuthenticated && !activationToken && resetToken && (
                    <PasswordResetForm
                        resetToken={resetToken}
                        onDone={clearResetTokenFromUrl}
                    />
                )}

                {!blockingAuthFlow && (
                    <Routes>
                        <Route
                            path={PATHS.splash}
                            element={<LandingPage />}
                        />
                        <Route
                            path={PATHS.login}
                            element={
                                isAuthenticated && isProfileCompleted ? (
                                    <Navigate to={homePath} replace />
                                ) : (
                                    <View style={AppStyles.appContainer}>
                                        <View style={AppStyles.authContainer}>
                                            <LoginForm
                                                key={locale}
                                                onCancel={() => navigate(PATHS.splash)}
                                                onSuccess={() => navigate(homePath, { replace: true })}
                                                onRegister={() => navigate(PATHS.register)}
                                            />
                                        </View>
                                    </View>
                                )
                            }
                        />
                        <Route
                            path={PATHS.register}
                            element={
                                isAuthenticated && isProfileCompleted ? (
                                    <Navigate to={homePath} replace />
                                ) : (
                                    <View style={AppStyles.appContainer}>
                                        <View style={AppStyles.authContainer}>
                                            <RegisterForm
                                                key={locale}
                                                telegramInviteToken={telegramInviteToken}
                                                onCancel={() => {
                                                    setTelegramInviteToken(null)
                                                    navigate(PATHS.splash)
                                                }}
                                                onSuccess={() => {
                                                    setTelegramInviteToken(null)
                                                    navigate(PATHS.splash)
                                                }}
                                                onPending={() => {
                                                    setTelegramInviteToken(null)
                                                    navigate(PATHS.splash)
                                                }}
                                            />
                                        </View>
                                    </View>
                                )
                            }
                        />
                        <Route path={PATHS.dashboard} element={requireStaff(renderStaffDashboard(null))} />
                        <Route path={PATHS.bills} element={requireStaff(renderStaffDashboard('bills'))} />
                        <Route path={PATHS.users} element={requireStaff(renderStaffDashboard('users'))} />
                        <Route path={PATHS.reports} element={requireStaff(renderStaffDashboard('reports'))} />
                        <Route path={PATHS.settings} element={requireStaff(renderStaffDashboard('settings'))} />
                        <Route
                            path={PATHS.notifications}
                            element={requireStaff(renderStaffDashboard('notifications'))}
                        />
                        <Route
                            path={PATHS.ratings}
                            element={requireStaff(renderStaffDashboard('ratings'))}
                        />
                        <Route
                            path={PATHS.video}
                            element={requireStaff(renderStaffDashboard('video'))}
                        />
                        <Route
                            path={PATHS.bookings}
                            element={requireStaff(
                                <BookingScreen
                                    onBack={() => {
                                        setPendingBookingReview(null)
                                        setBookingEditContext(null)
                                        navigate(PATHS.dashboard, { replace: true })
                                    }}
                                    pendingReview={pendingBookingReview}
                                    onPendingReviewComplete={() => setPendingBookingReview(null)}
                                    bookingEdit={bookingEditContext}
                                    onBookingEditComplete={() => setBookingEditContext(null)}
                                />,
                            )}
                        />
                        <Route
                            path={PATHS.rivalSearch}
                            element={requireStaff(
                                <StaffRivalSearchScreen
                                    onClose={() => navigate(PATHS.dashboard, { replace: true })}
                                />,
                            )}
                        />
                        <Route
                            path={PATHS.home}
                            element={requireUserLanding(
                                <UserHomeScreen
                                    onOpenCabinet={() => navigate(PATHS.cabinet)}
                                    onBackToStaff={
                                        canManageTables
                                            ? () => {
                                                dispatch(setStaffViewMode('admin'))
                                                navigate(PATHS.dashboard)
                                            }
                                            : undefined
                                    }
                                />,
                            )}
                        />
                        <Route
                            path={PATHS.cabinet}
                            element={
                                !isAuthenticated ? (
                                    <Navigate to={PATHS.login} replace />
                                ) : !profileReady ? null : (
                                    <UserCabinetScreen onBack={() => navigate(PATHS.home)} />
                                )
                            }
                        />
                        <Route
                            path={PATHS.score}
                            element={requireService(<ServiceScoreBoard />)}
                        />
                        <Route
                            path="*"
                            element={(
                                <Navigate
                                    to={
                                        isAuthenticated && isProfileCompleted
                                            ? homePath
                                            : PATHS.splash
                                    }
                                    replace
                                />
                            )}
                        />
                    </Routes>
                )}
                </React.Suspense>
            </KeyboardAvoidingView>
        </SafeAreaView>
    )
}
